import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute } from '@angular/router';
import { LocationValue, SearchEngineFieldLocation } from '@domains/search-engine/entities/location-search-field';
import { SearchEngineQueryParams } from '@domains/search-engine/search-engine-field';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EventWebservice } from '@webservices/event-api/event.webservice';
import { NgUpperFirstPipeModule } from '@wizbii/angular-pipes';
import { City } from '@wizbii/models';
import { CityWebservice } from '@wizbii/webservices';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map, shareReplay, switchMap, take } from 'rxjs/operators';

enum WorkplaceTypeEnum {
  city = 'city',
  department = 'department',
  region = 'region',
}

@UntilDestroy()
@Component({
  selector: 'app-location-search',
  templateUrl: './location-search.component.html',
  styleUrls: ['./location-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationSearchComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationSearchComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() field!: SearchEngineFieldLocation;
  @Input() modalOpen!: boolean;
  @Input() liveSearch!: boolean;

  @Output() hasChanged = new EventEmitter<boolean>();

  private _value?: LocationValue;

  locationIsFocused = false;

  workplaceType$ = new BehaviorSubject<WorkplaceTypeEnum | undefined>(undefined);
  selectedLocation$ = new BehaviorSubject<string | undefined>(undefined);

  cities$ = new Observable<City[]>();
  departments$ = new Observable<string[]>();
  regions$ = new Observable<string[]>();
  error$ = new BehaviorSubject<boolean>(false);

  WorkplaceTypeEnum = WorkplaceTypeEnum;

  locationForm = new FormGroup({ location: new FormControl() });

  set value(val: LocationValue | undefined) {
    if (this._value === val) {
      return;
    }
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  get value(): LocationValue | undefined {
    return this.value;
  }

  get location(): FormControl {
    return this.locationForm.get('location') as FormControl;
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly cityWebservice: CityWebservice,
    private readonly eventWebService: EventWebservice,
    private readonly route: ActivatedRoute,
    private readonly formBuilder: FormBuilder
  ) {
    combineLatest([
      this.selectedLocation$.pipe(distinctUntilChanged()),
      this.workplaceType$.pipe(distinctUntilChanged()),
    ])
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ([location, type]) => {
          this.writeValue({
            name:
              type === WorkplaceTypeEnum.city
                ? 'cityPlaceId'
                : type === WorkplaceTypeEnum.department
                ? 'departement'
                : 'region',
            value: location ?? '',
          });
        },
      });
  }

  ngOnInit(): void {
    combineLatest([
      this.route.queryParamMap.pipe(
        map((params) => params.get(SearchEngineQueryParams.CityPlaceId)),
        distinctUntilChanged(),
        switchMap((cityPlaceId) => (cityPlaceId ? this.cityWebservice.get(cityPlaceId) : of(null))),
        shareReplay({ bufferSize: 1, refCount: true })
      ),
      this.route.queryParamMap.pipe(map((q) => q.get(SearchEngineQueryParams.Department))),
      this.route.queryParamMap.pipe(map((q) => q.get(SearchEngineQueryParams.Region))),
    ])
      .pipe(take(1))
      .subscribe({
        next: ([city, departement, region]: any) => {
          this.locationForm = this.formBuilder.group({
            location: [this.getLocation(city, departement, region)],
          });

          this.cdr.detectChanges();
        },
      });

    const distinctLocationChanges$ = this.location.valueChanges.pipe(
      debounceTime(300),
      untilDestroyed(this),
      filter((query) => query && query.length > 0),
      distinctUntilChanged()
    );

    this.cities$ = distinctLocationChanges$.pipe(
      switchMap((query) =>
        this.cityWebservice.getBy(this.field.limitFrance ? { query, countries: ['FR'] } : { query })
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    if (this.field.hasRegionAndDepartment) {
      this.departments$ = distinctLocationChanges$.pipe(
        switchMap((query) => this.eventWebService.getFacetValue('location.department', query)),
        shareReplay({ bufferSize: 1, refCount: true })
      );

      this.regions$ = distinctLocationChanges$.pipe(
        switchMap((query) => this.eventWebService.getFacetValue('location.state', query)),
        shareReplay({ bufferSize: 1, refCount: true })
      );

      combineLatest([this.cities$, this.departments$, this.regions$])
        .pipe(untilDestroyed(this))
        .subscribe({
          next: ([cities, departements, regions]) => {
            const noLocation = cities?.length === 0 && departements?.length === 0 && regions?.length === 0;
            this.error$.next(noLocation);
          },
        });
    } else {
      this.cities$.pipe(untilDestroyed(this)).subscribe({
        next: (cities) => {
          this.error$.next(cities?.length === 0);
        },
      });
    }
  }

  onClose(): void {
    if (this._value) {
      return;
    }

    this.cities$
      .pipe(
        first(),
        map((cities) => cities[0]),
        filter((city) => !!city)
      )
      .subscribe({ next: (city) => this.selectOptionLocation(city, WorkplaceTypeEnum.city) });
  }

  clearValue(): void {
    this.workplaceType$.next(undefined);
    this.selectedLocation$.next(undefined);
    this.location.setValue('');
    this.error$.next(false);

    this.value = undefined;

    if (this.liveSearch) {
      this.hasChanged.emit();
    }
  }

  selectOptionLocation(location: City | string, type: WorkplaceTypeEnum): void {
    if (location) {
      this.workplaceType$.next(type);
      this.selectedLocation$.next(typeof location === 'object' ? location.cityPlaceId : location);

      this.location?.setValue(typeof location === 'object' ? `${location.city}, ${location.department}` : location);
    }

    if (this.liveSearch) {
      this.hasChanged.emit();
    }

    this.cdr.detectChanges();
  }

  trackByString(_: number, str: string): string {
    return str;
  }

  displayFn(location: City | string): string {
    return typeof location === 'object' ? location?.city : location;
  }

  onChange: (val?: LocationValue) => void = () => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  writeValue(val?: LocationValue): void {
    this.value = val;
  }

  registerOnChange(fn: (val?: LocationValue) => void): void {
    this.onChange = (val) => {
      fn(val);
      this.cdr.markForCheck();
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  trackByCity(_: number, city: City): string {
    return city.city;
  }

  ngOnDestroy(): void {
    this.workplaceType$.complete();
    this.selectedLocation$.complete();
    this.error$.complete();
  }

  private getLocation(city: City, departement: string, region: string): string | void {
    if (city) {
      return `${city.city}, ${city.department}`;
    }
    if (departement) {
      return departement;
    }
    if (region) {
      return region;
    }
  }
}

@NgModule({
  imports: [
    CommonModule,
    MatIconModule,
    MatOptionModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    NgUpperFirstPipeModule,
  ],
  declarations: [LocationSearchComponent],
  exports: [LocationSearchComponent],
})
export class SearchEngineLocationSearchModule {}
